import { BrowserRouter, Route, Routes, useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { BuilderShell } from '../shell/BuilderShell';
import { DEFAULT_ROUTE } from '../lib/builderApi';
import { useBuilderStore } from '../state/builderStore';
import { useGenerationStore } from '../state/generation/generationStore';

/**
 * The one editor route in v1 (build-plan §2.4): a thin react-router over the query params
 * `?template=&handle=&locale=` (defaults `index`/`default`/`fr`, matching `BuilderPageController`).
 *
 * `store_id` is NEVER a route param — it comes only from the JWT (build-plan §2.4, §3.7). On mount/reload
 * the URL drives the load; thereafter, when the Topbar switches the open template (via the store), the URL
 * is MIRRORED to match so a RELOAD restores the template you were on instead of bouncing to home.
 */
export function EditorRoute() {
  const [params] = useSearchParams();
  const load = useBuilderStore((s) => s.load);
  const applyTheme = useBuilderStore((s) => s.applyTheme);
  const route = useBuilderStore((s) => s.route);

  const template = params.get('template') ?? DEFAULT_ROUTE.template;
  const handle = params.get('handle') ?? DEFAULT_ROUTE.handle;
  // No fallback: an absent `?locale=` means "the store's own language" (feature 0050 lot 0).
  const locale = params.get('locale') ?? undefined;
  const applyThemeId = params.get('applyTheme');
  // `?resetTheme=1` : re-semer le workspace du thème depuis le registre au lieu de rouvrir le
  // brouillon existant. Nécessaire parce qu'un brouillon sans estampille (semé avant que
  // l'estampille existe) ne peut pas être distingué du travail du vendeur — sans cette sortie,
  // le workspace reste figé sur une version du thème vieille de plusieurs semaines.
  const resetTheme = params.get('resetTheme') === '1';
  // `?generate=<categoryId>` (feature 0059 lot 1d): arm the generation panel over the theme-workspace
  // boot. Typically paired with `?applyTheme=<themeIdFor(category)>` so the seed and the scene share a
  // boot. Armed BEFORE the strip (the panel outlives the URL param); the run itself only starts when
  // the seller submits the intro form — a stale bookmarked URL can never fire a paid generation.
  const generateCategory = params.get('generate');
  // `?brief=<phrase>` rides along with `generate`: the seller typed her phrase ONCE, on the dashboard's
  // AI card — the builder panel arrives pre-filled and she only confirms. Stripped with `generate`.
  const generateBrief = params.get('brief');
  // `?autostart=1` (the dashboard AI-card flow): the ONE question was already asked there — the
  // takeover starts on arrival, no second confirm. Never set by hand-typed URLs; without it the
  // in-editor panel with its confirm click is the flow.
  const generateAutostart = params.get('autostart') === '1';
  // `?product=<id>`: the product the store is built AROUND (founder 2026-08-20) — grounds the copy,
  // the hero image and the product-page preview.
  const generateProduct = params.get('product');
  // `?run=<id>`: minted per «Générer» click by the dashboard — keys the completion guard so a reload
  // of THIS iframe never re-fires the paid run, while a new click (new id) always runs.
  const generateRun = params.get('run');

  // Apply-theme boot (theme registry, spec 0032): `?applyTheme=<id>` seeds the store's DRAFTS from the
  // registry theme + stages its ThemeSettings, then opens the index template (all inside
  // builderStore.applyTheme — unknown ids and failures fall back to a normal load there). The param is
  // stripped from the URL BEFORE the async work starts, so a refresh mid-apply boots normally instead of
  // re-applying. Raw replaceState (like the mirror below) fires no popstate, so react-router's `params`
  // keep the STALE applyTheme value — hence the ref guard, which also absorbs StrictMode's double-effect
  // (mirrors BuilderBoot). A later real navigation re-parses the stripped URL → normal loads resume.
  const applyThemeStarted = useRef(false);

  // Generation arming (0059): same one-shot + strip discipline as applyTheme, own ref guard because the
  // two params ride the same URL but have independent lifecycles (StrictMode double-effect included).
  const generateArmed = useRef(false);
  useEffect(() => {
    if (!generateCategory || generateArmed.current) return;
    generateArmed.current = true;
    useGenerationStore
      .getState()
      .arm(generateCategory, generateBrief ?? '', generateAutostart, generateProduct, generateRun);
    const qs = new URLSearchParams(window.location.search);
    qs.delete('generate');
    qs.delete('brief');
    qs.delete('autostart');
    qs.delete('product');
    qs.delete('run');
    const rest = qs.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${rest ? `?${rest}` : ''}`,
    );
  }, [generateCategory, generateBrief, generateAutostart, generateProduct, generateRun]);

  // URL → store: load the route the URL names (mount, reload, browser back/forward). Errors are reflected
  // into the store's loadState (the shell renders no-store / session-expired / error); swallow the rejection
  // so it isn't an unhandled promise.
  useEffect(() => {
    if (applyThemeId) {
      if (!applyThemeStarted.current) {
        applyThemeStarted.current = true;
        const qs = new URLSearchParams(window.location.search);
        qs.delete('applyTheme');
        qs.delete('resetTheme');
        const rest = qs.toString();
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${rest ? `?${rest}` : ''}`,
        );
        void applyTheme(applyThemeId, { template, handle, locale }, { reset: resetTheme }).catch(
          () => undefined,
        );
      }
      // While router memory still holds the stale param, apply-theme owns the boot load.
      return;
    }
    // Normal boot/navigation. Theme-workspace staging syncs inside load() itself (its single
    // navigation chokepoint), so nothing extra is needed here.
    void load({ template, handle, locale }).catch(() => undefined);
  }, [load, applyTheme, template, handle, locale, applyThemeId, resetTheme]);

  // store → URL: mirror the OPEN template into the address bar via a RAW history.replaceState — deliberately
  // NOT react-router's setSearchParams. replaceState fires no popstate, so react-router's `params` don't
  // change and the URL→store effect above does NOT re-run: there is no two-way feedback loop. The FIRST
  // observed route is skipped (on mount/reload the URL is authoritative — we must not overwrite it with the
  // store's default), so this writes only once the seller actually switches templates → the reload is sticky.
  const lastRouteKey = useRef<string | null>(null);
  useEffect(() => {
    const key = `${route.template}|${route.handle}|${route.locale}`;
    if (lastRouteKey.current === key) return;
    const firstObservation = lastRouteKey.current === null;
    lastRouteKey.current = key;
    if (firstObservation) return;
    const qs = routeParams(route);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${qs}`);
  }, [route]);

  return <BuilderShell />;
}

/** Route → query string. `locale` is omitted when absent: the backend then resolves the STORE's
 *  own language (feature 0050 lot 0). Sending an empty value would read as an invalid locale. */
function routeParams(route: {
  template: string;
  handle: string;
  locale?: string;
}): URLSearchParams {
  const qs = new URLSearchParams({ template: route.template, handle: route.handle });
  if (route.locale) qs.set('locale', route.locale);
  return qs;
}

export function BuilderRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<EditorRoute />} />
        <Route path="*" element={<EditorRoute />} />
      </Routes>
    </BrowserRouter>
  );
}
